import { getIdToken } from './auth';

const API = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

async function authHeaders() {
  const token = await getIdToken();
  if (!token) throw new Error('Not signed in');
  return { Authorization: `Bearer ${token}` };
}

async function parseJson(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

/**
 * List saved signatures for the signed-in account, scoped to one library.
 * `fieldKind` selects which library: 'signature' (default) or 'initials'.
 * Omitting it preserves pre-24.1 behavior — the server returns the signatures
 * library only. Returns { signatures, plan, cap, used } — plan/cap come from
 * the server so the UI can render accurate affordances without a second RTT.
 */
export async function listSignatures(fieldKind = 'signature') {
  const url = new URL(`${API}/signatures`);
  if (fieldKind) url.searchParams.set('kind', fieldKind);
  const res = await fetch(url.toString(), {
    headers: await authHeaders(),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    const err = new Error(data.message || 'Could not load signatures.');
    err.status = res.status;
    throw err;
  }
  return {
    signatures: data.signatures || [],
    plan: data.plan || 'lite',
    cap: typeof data.cap === 'number' ? data.cap : FALLBACK_CAP,
    used: typeof data.used === 'number' ? data.used : (data.signatures || []).length,
  };
}

/**
 * Persist a new signature for the signed-in account. `kind` is the creation
 * method (draw | type | upload). `fieldKind` routes it into the signature or
 * initials library (default: signature). Throws `code: 'SIGNATURES_CAP_REACHED'`
 * when the plan cap is hit — error carries `plan`, `cap`, `used`.
 */
export async function createSignature({ kind, fieldKind = 'signature', label, dataUrl, color, strokeWidth }) {
  const res = await fetch(`${API}/signatures`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ kind, fieldKind, label, dataUrl, color, strokeWidth }),
  });
  const data = await parseJson(res);
  if (!res.ok) {
    const err = new Error(data.message || 'Could not save signature.');
    err.status = res.status;
    err.code = data.code;
    err.plan = data.plan;
    err.cap = data.cap;
    err.used = data.used;
    throw err;
  }
  return data.signature;
}

/** Delete a saved signature by id. */
export async function deleteSignature(id) {
  const res = await fetch(`${API}/signatures/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    const err = new Error(data.message || 'Could not delete signature.');
    err.status = res.status;
    throw err;
  }
}

// Client-side fallback when the server doesn't return a cap (offline, old API).
// Matches the server's safety default.
export const FALLBACK_CAP = 1;
